import React from 'react'
import { Link, useNavigate } from 'react-router-dom'
import useSWR from 'swr'
import Select from 'react-select'
import { fetchVodAssets, fetchVodTypes } from '../services/vodApi'
import { Badge, Button, FormattedDateTime, Pagination } from '../components'
import { useVodsFilters } from '../hooks'
import {
  VodAsset,
  VodOrigin,
  VodAvailabilityState,
  VodStatus,
} from '../types/vod'
import pageStyles from '../styles/page.module.scss'
import listTableStyles from '../styles/list-table.module.scss'
import selectStyles from '../styles/react-select.module.scss'
import styles from './VodsList.module.scss'

const ORIGIN_OPTIONS = [
  { value: '', label: 'All Sources' },
  { value: 'AMAGI', label: 'Amagi' },
  { value: 'WSC', label: 'WSC' },
  { value: 'TENNIS_CHANNEL', label: 'Tennis Channel' },
  { value: 'VIRTUAL', label: 'Virtual' },
  { value: 'UNKNOWN', label: 'Unknown' },
] as const

const AVAILABILITY_OPTIONS = [
  { value: '', label: 'All Availability' },
  { value: 'AVAILABLE', label: 'Available' },
  { value: 'UPCOMING', label: 'Upcoming' },
  { value: 'EXPIRED', label: 'Expired' },
] as const

const STATUS_OPTIONS = [
  { value: '', label: 'All Statuses' },
  { value: 'ACTIVE', label: 'Active' },
  { value: 'EXPIRED', label: 'Expired' },
  { value: 'HIDDEN', label: 'Hidden' },
] as const

const ORIGIN_VARIANT: Record<VodOrigin, 'primary' | 'secondary' | 'neutral'> = {
  AMAGI: 'secondary',
  WSC: 'secondary',
  TENNIS_CHANNEL: 'primary',
  VIRTUAL: 'neutral',
  UNKNOWN: 'neutral',
}

const AVAILABILITY_VARIANT: Record<
  VodAvailabilityState,
  'success' | 'warning' | 'error' | 'neutral'
> = {
  AVAILABLE: 'success',
  UPCOMING: 'warning',
  EXPIRED: 'error',
  UNKNOWN: 'neutral',
}

const VodsList: React.FC = () => {
  const navigate = useNavigate()
  const { filters, updateFilterAndResetPage, handlePageChange } = useVodsFilters()
  const { vodTypeCode, origin, availability, status, search, matchId, page } =
    filters
  const limit = 50

  // VOD types (for the type filter) come from the DB, not a hardcoded list
  const { data: typesData } = useSWR('vod-types', () => fetchVodTypes(), {
    revalidateOnFocus: false,
  })
  const typeOptions = [
    { value: '', label: 'All Types' },
    ...(typesData?.types || []).map((t) => ({
      value: t.vod_type_code,
      label: t.display_name,
    })),
  ]

  const { data, isLoading, error, mutate } = useSWR(
    ['vods', { vodTypeCode, origin, availability, status, search, matchId, page, limit }],
    () =>
      fetchVodAssets({
        vod_type_code: vodTypeCode || undefined,
        origin: (origin as VodOrigin) || undefined,
        availability: (availability as VodAvailabilityState) || undefined,
        status: (status as VodStatus) || undefined,
        search: search || undefined,
        match_id: matchId || undefined,
        page,
        limit,
      }),
    { revalidateOnFocus: false }
  )

  const assets: VodAsset[] = data?.assets || []
  const pagination = data?.pagination

  const formatDuration = (seconds?: number) => {
    if (!seconds) return 'N/A'
    const mins = Math.floor(seconds / 60)
    const secs = Math.floor(seconds % 60)
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  const originLabel = (o?: VodOrigin) =>
    ORIGIN_OPTIONS.find((opt) => opt.value === o)?.label || o || '—'

  if (error) {
    return (
      <div className={pageStyles.pageContainer}>
        <div className={pageStyles.error}>
          <h2>Error loading VODs</h2>
          <p>Failed to fetch VODs: {(error as Error).message}</p>
          <Button variant="secondary" onClick={() => mutate()}>
            Retry
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className={pageStyles.pageContainer}>
      <div className={pageStyles.pageHeader}>
        <h1>VODs</h1>
      </div>

      {matchId && (
        <div className={styles.matchFilterBanner}>
          <span>
            Showing VODs for match:{' '}
            <code className={styles.matchIdCode}>{matchId}</code>
          </span>
          <Button
            variant="secondary"
            size="small"
            onClick={() => updateFilterAndResetPage({ matchId: '' })}
          >
            Clear Filter
          </Button>
          <Link to={`/matches/${matchId}`}>
            <Button variant="secondary" size="small">
              View Match
            </Button>
          </Link>
        </div>
      )}

      <div className={listTableStyles.searchSection}>
        <div className={listTableStyles.searchForm}>
          <div className={styles.filters}>
            <div className={styles.field}>
              <label htmlFor="search">Search:</label>
              <input
                type="text"
                id="search"
                value={search}
                onChange={(e) =>
                  updateFilterAndResetPage({ search: e.target.value })
                }
                placeholder="Title, Asset ID, or Description"
                className={listTableStyles.searchInput}
              />
            </div>
            <div className={styles.field}>
              <label htmlFor="vodTypeCode">Type:</label>
              <Select
                inputId="vodTypeCode"
                value={
                  typeOptions.find((opt) => opt.value === vodTypeCode) ||
                  typeOptions[0]
                }
                onChange={(option) =>
                  updateFilterAndResetPage({ vodTypeCode: option?.value || '' })
                }
                options={typeOptions}
                className={selectStyles.reactSelect}
                classNamePrefix="react-select"
                isClearable={false}
              />
            </div>
            <div className={styles.field}>
              <label htmlFor="origin">Source:</label>
              <Select
                inputId="origin"
                value={
                  ORIGIN_OPTIONS.find((opt) => opt.value === origin) ||
                  ORIGIN_OPTIONS[0]
                }
                onChange={(option) =>
                  updateFilterAndResetPage({
                    origin: (option?.value || '') as VodOrigin | '',
                  })
                }
                options={ORIGIN_OPTIONS}
                className={selectStyles.reactSelect}
                classNamePrefix="react-select"
                isClearable={false}
              />
            </div>
            <div className={styles.field}>
              <label htmlFor="availability">Availability:</label>
              <Select
                inputId="availability"
                value={
                  AVAILABILITY_OPTIONS.find(
                    (opt) => opt.value === availability
                  ) || AVAILABILITY_OPTIONS[0]
                }
                onChange={(option) =>
                  updateFilterAndResetPage({
                    availability: (option?.value ||
                      '') as VodAvailabilityState | '',
                  })
                }
                options={AVAILABILITY_OPTIONS}
                className={selectStyles.reactSelect}
                classNamePrefix="react-select"
                isClearable={false}
              />
            </div>
            <div className={styles.field}>
              <label htmlFor="status">Status:</label>
              <Select
                inputId="status"
                value={
                  STATUS_OPTIONS.find((opt) => opt.value === status) ||
                  STATUS_OPTIONS[0]
                }
                onChange={(option) =>
                  updateFilterAndResetPage({
                    status: (option?.value || '') as VodStatus | '',
                  })
                }
                options={STATUS_OPTIONS}
                className={selectStyles.reactSelect}
                classNamePrefix="react-select"
                isClearable={false}
              />
            </div>
          </div>
        </div>
      </div>

      {isLoading && !data ? (
        <div className={pageStyles.loading}>
          <h3>Loading VODs...</h3>
          <p>Please wait while we fetch the catalog.</p>
        </div>
      ) : assets.length === 0 ? (
        <div className={listTableStyles.noResults}>
          <h3>No VODs found</h3>
          <p>No VODs are available for the selected filters.</p>
        </div>
      ) : (
        <>
          <div className={`${listTableStyles.tableHeader} ${styles.listHeader}`}>
            <div>Title</div>
            <div>Type</div>
            <div>Source</div>
            <div>Availability</div>
            <div>Playable</div>
            <div>Match</div>
            <div>Updated</div>
            <div></div>
          </div>

          <div className={listTableStyles.tableBody}>
            {assets.map((asset) => (
              <div
                key={asset.tc_vod_asset_id}
                className={`${listTableStyles.tableRow} ${styles.listItem}`}
              >
                <div className={listTableStyles.cellPrimary}>
                  <Link
                    to={`/vods/${asset.tc_vod_asset_id}`}
                    className={styles.titleLink}
                  >
                    <p className={listTableStyles.primaryText}>
                      {asset.tc_title}
                    </p>
                  </Link>
                  <p className={listTableStyles.secondaryText}>
                    {formatDuration(asset.duration_sec)}
                  </p>
                </div>

                <div className={listTableStyles.cellText}>
                  {asset.vod_type_display_name || asset.vod_type_code || 'N/A'}
                </div>

                <div className={listTableStyles.cellText}>
                  <Badge variant={ORIGIN_VARIANT[asset.origin || 'UNKNOWN']}>
                    {originLabel(asset.origin)}
                  </Badge>
                </div>

                <div className={listTableStyles.cellText}>
                  <Badge
                    variant={
                      AVAILABILITY_VARIANT[
                        asset.availability_state || 'UNKNOWN'
                      ]
                    }
                  >
                    {asset.availability_state || 'UNKNOWN'}
                  </Badge>
                </div>

                <div className={listTableStyles.cellText}>
                  {asset.has_playable_rendition ? (
                    <Badge variant="success">Playable</Badge>
                  ) : (
                    <Badge variant="neutral">None</Badge>
                  )}
                </div>

                <div className={listTableStyles.cellText}>
                  {asset.match?.tc_match_id ? (
                    <Link
                      to={`/matches/${asset.match.tc_match_id}`}
                      className={styles.matchLink}
                    >
                      <Badge variant="success" icon="link">
                        Linked
                      </Badge>
                    </Link>
                  ) : (
                    <Badge variant="neutral">Unlinked</Badge>
                  )}
                </div>

                <div className={listTableStyles.cellText}>
                  <FormattedDateTime dateString={asset.updated_at} />
                </div>

                <div className={styles.actionsCell}>
                  <Button
                    variant="secondary"
                    size="small"
                    onClick={() => navigate(`/vods/${asset.tc_vod_asset_id}`)}
                  >
                    View
                  </Button>
                </div>
              </div>
            ))}
          </div>

          {pagination && pagination.total_pages > 1 && (
            <Pagination pagination={pagination} onPageChange={handlePageChange} />
          )}
        </>
      )}
    </div>
  )
}

export default VodsList
